import React, { useState, useRef } from "react";
import { useMutation, useQuery } from "@apollo/client";
import imageService from "../../services/image";
import streamService from "../../services/streamService";
import { Box } from "@mui/system";
import RichTextEditor from "react-rte";

import {
  ALL_VIDEOS,
  CREATE_VIDEOS,
  CREATE_VIDEO_MUSCLE_TYPE_LIST,
  UPDATE_VIDEOS,
} from "../../gql/video";

import {
  Button,
  Typography,
  Card,
  CardMedia,
  TextField,
  FormControl,
  MenuItem,
  InputLabel,
  Select,
  FormHelperText,
  CircularProgress,
  Alert,
  ListItemText,
  OutlinedInput,
  Checkbox,
  Divider,
  Chip,
} from "@mui/material";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import VideoFileIcon from "@mui/icons-material/VideoFile";
import ImageIcon from "@mui/icons-material/Image";
import {
  EXE_TYPE,
  EXP_LEVEL,
  MUSCLE_TYPE,
  SUBSCRIPTION_TYPE,
} from "../../gql/mixed";

const sectionCard = {
  borderRadius: "12px",
  border: "1px solid #eee",
  boxShadow: "0 1px 4px rgba(0,0,0,0.04)",
  p: 3,
  mb: 2.5,
};

const UploadZone = ({ label, accept, onChange, preview, uploading, error, isVideo, isUploaded }) => {
  const ref = useRef(null);
  return (
    <Box sx={{ flex: 1, minWidth: 160 }}>
      <Box
        onClick={() => ref.current?.click()}
        sx={{
          height: 160,
          border: error ? "2px dashed #d32f2f" : isUploaded ? "2px dashed #2e7d32" : "2px dashed #ddd",
          borderRadius: "12px",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          cursor: uploading ? "not-allowed" : "pointer",
          bgcolor: isUploaded ? "rgba(46,125,50,0.03)" : "#fafafa",
          overflow: "hidden",
          transition: "all 0.2s",
          "&:hover": !uploading ? { borderColor: "#ff5722", bgcolor: "rgba(255,87,34,0.03)" } : {},
        }}
      >
        {uploading ? (
          <CircularProgress size={28} sx={{ color: "#ff5722" }} />
        ) : preview && !isVideo ? (
          <Box component="img" src={preview} alt="preview" sx={{ width: "100%", height: "100%", objectFit: "cover" }} />
        ) : preview && isVideo ? (
          <Box component="video" src={preview} sx={{ width: "100%", height: "100%", objectFit: "cover" }} />
        ) : (
          <>
            {isVideo ? (
              <VideoFileIcon sx={{ fontSize: 34, color: "#bbb", mb: 0.5 }} />
            ) : (
              <CloudUploadIcon sx={{ fontSize: 34, color: "#bbb", mb: 0.5 }} />
            )}
            <Typography variant="body2" color="text.secondary" textAlign="center" px={1} fontSize="0.78rem">
              {label}
            </Typography>
            <Typography variant="caption" color="text.disabled" fontSize="0.68rem">
              {accept}
            </Typography>
          </>
        )}
      </Box>
      <input ref={ref} type="file" accept={isVideo ? "video/mp4,video/webm" : "image/*"} onChange={onChange} style={{ display: "none" }} />
      {isUploaded && !uploading && <Chip label="Uploaded ✓" color="success" size="small" sx={{ mt: 0.5, fontSize: "0.7rem" }} />}
      {uploading && <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>Uploading...</Typography>}
      {error && <Typography variant="caption" color="error" sx={{ mt: 0.5, display: "block" }}>{error}</Typography>}
    </Box>
  );
};

const CreateVideo = ({ handleClose }) => {
  const MenuProps = {
    PaperProps: { style: { width: 250, maxHeight: "80vh" } },
  };

  const fileTypes = ["video/webm", "video/mp4"];
  const thumbnailfileTypes = [
    "image/apng", "image/bmp", "image/gif", "image/jpeg", "image/pjpeg",
    "image/png", "image/svg+xml", "image/tiff", "image/webp",
  ];

  const [imagePreview, setImagePreview] = useState();
  const [imageFile, setImageFile] = useState();
  const [videoAFile, setVideoAFile] = useState("");
  const [videoAPreview, setVideoAPreview] = useState();
  const [videoBFile, setVideoBFile] = useState("");
  const [videoBPreview, setVideoBPreview] = useState();
  const [uploadingField, setUploadingField] = useState(null);
  const [loading, setLoading] = useState(false);
  const [values, setValues] = useState({});
  const [errors, setErrors] = useState({});
  const [showAlert, setShowAlert] = useState({ message: "", isError: false });
  const [textValue, setTextValue] = useState(RichTextEditor.createEmptyValue());
  const [textValueMM, setTextValueMM] = useState(RichTextEditor.createEmptyValue());
  const [progress, setProgress] = useState(0);
  const [muscleType, setMuscleType] = useState([]);
  const pendingVideoUrlsRef = useRef({ video_url_a: null, video_url_b: null });

  const { data: exeTypes } = useQuery(EXE_TYPE);
  const { data: expLevels } = useQuery(EXP_LEVEL);
  const { data: muslceTypess } = useQuery(MUSCLE_TYPE);
  const { data: subscriptionTypes } = useQuery(SUBSCRIPTION_TYPE);

  const handleChanges = (event) => {
    const { target: { value } } = event;
  };

  const handleChangesMuscleType = (event) => {
    const { target: { value } } = event;
    setMuscleType((prevSubType) => {
      const newValue = typeof value === "string" ? value.split(",") : value;
      return newValue;
    });
  };

  const getSubtypeNameById = (uuid) => {
    const subtype = muslceTypess?.muscle_types.find((subtype) => subtype.id === uuid);
    return subtype ? subtype.name : "";
  };

  const handleChange = (prop) => (event) => {
    setValues((prev) => ({ ...prev, [prop]: event.target.value }));
  };

  const [createVideoSubType] = useMutation(CREATE_VIDEO_MUSCLE_TYPE_LIST, {
    onError: (err) => console.log("Error ", err),
    onCompleted: (res) => console.log("Result ", res),
  });

  const [updateVideo] = useMutation(UPDATE_VIDEOS, {
    onError: (err) => console.error("Update after segment failed:", err),
    refetchQueries: [ALL_VIDEOS],
  });

  const [createVideo] = useMutation(CREATE_VIDEOS, {
    onError: (error) => {
      console.log("error : ", error);
      alert("Video Upload Error");
      setLoading(false);
    },
    onCompleted: async (result) => {
      const id = result?.insert_video_list_one?.id;
      const videoUrlA = pendingVideoUrlsRef.current.video_url_a;
      const videoUrlB = pendingVideoUrlsRef.current.video_url_b;
      muscleType?.map(async (item) => {
        await createVideoSubType({ variables: { fk_muscle_type_id: item, fk_video_list_id: id } });
      });
      setValues({});
      setErrors({});
      setImageFile("");
      setImagePreview("");
      setLoading(false);
      pendingVideoUrlsRef.current = { video_url_a: null, video_url_b: null };
      if (id && (videoUrlA || videoUrlB)) {
        try {
          let manifestUrlA = videoUrlA;
          let manifestUrlB = videoUrlB;
          if (videoUrlA) {
            const sourceKey = streamService.getSourceKeyFromUploadUrl(videoUrlA);
            if (sourceKey) {
              const res = await streamService.segmentVideo({ videoId: `${id}-a`, sourceKey });
              manifestUrlA = res.manifestUrl;
            }
          }
          if (videoUrlB) {
            const sourceKeyB = streamService.getSourceKeyFromUploadUrl(videoUrlB);
            if (sourceKeyB) {
              const resB = await streamService.segmentVideo({ videoId: `${id}-b`, sourceKey: sourceKeyB });
              manifestUrlB = resB.manifestUrl;
            }
          }
          await updateVideo({
            variables: {
              id,
              video_package_name: values.video_package_name || "",
              duration_in_minute: values.duration_in_minute ?? 0,
              video_url_a: manifestUrlA || null,
              video_url_b: manifestUrlB || null,
              thumbnail_image_url: values.thumbnail_image_url || "",
              promotion: values.promotion === "true" || values.promotion === true,
              description: values.description || "",
              description_mm: values.description_mm || "",
              fk_exercise_type_name: values.fk_exercise_type_name || "",
              fk_experience_level_id: values.fk_experience_level_id || "",
              fk_user_subscription_level_id: values.fk_user_subscription_level_id ?? 1,
            },
          });
        } catch (err) {
          console.error("HLS segment/update after create failed:", err);
        }
      }
      alert("New Video has been Created");
      handleClose();
    },
    refetchQueries: [ALL_VIDEOS],
  });

  const thumbnailImageChange = async (e) => {
    if (e.target.files && e.target.files[0]) {
      let thumbImg = e.target.files[0];
      if (!thumbnailfileTypes.includes(thumbImg.type)) {
        setErrors((prev) => ({ ...prev, thumbnail_image_url: "Please select images. (JPEG,PNG,...)" }));
        return;
      }
      if (thumbImg.size > 10485760) {
        setErrors((prev) => ({ ...prev, thumbnail_image_url: "Image file size must be smaller than 10MB." }));
        return;
      }
      setImageFile(thumbImg);
      setImagePreview(URL.createObjectURL(thumbImg));
      setUploadingField("thumbnail");
      setErrors((prev) => ({ ...prev, thumbnail_image_url: "" }));
      try {
        const result = await imageService.uploadFile(thumbImg);
        setValues((prev) => ({ ...prev, thumbnail_image_url: result.imageUploadUrl }));
      } catch (err) {
        setErrors((prev) => ({ ...prev, thumbnail_image_url: err?.message || "Upload failed." }));
      } finally {
        setUploadingField(null);
      }
    }
  };

  const videoChangeA = async (e) => {
    if (e.target.files && e.target.files[0]) {
      let videofile = e.target.files[0];
      if (!fileTypes.includes(videofile.type)) {
        setErrors((prev) => ({ ...prev, video_url_a: "Please select video. (mp4,webm,...)" }));
        return;
      }
      setVideoAFile(videofile);
      setVideoAPreview(URL.createObjectURL(videofile));
      setUploadingField("videoA");
      setErrors((prev) => ({ ...prev, video_url_a: "" }));
      try {
        const result = await imageService.uploadFile(videofile);
        setValues((prev) => ({ ...prev, video_url_a: result.imageUploadUrl }));
      } catch (err) {
        setErrors((prev) => ({ ...prev, video_url_a: err?.message || "Upload failed." }));
      } finally {
        setUploadingField(null);
      }
    }
  };

  const videoChangeB = async (e) => {
    if (e.target.files && e.target.files[0]) {
      let videofile = e.target.files[0];
      if (!fileTypes.includes(videofile.type)) {
        setErrors((prev) => ({ ...prev, video_url_b: "Please select video. (mp4,webm,)" }));
        return;
      }
      setVideoBFile(videofile);
      setVideoBPreview(URL.createObjectURL(videofile));
      setUploadingField("videoB");
      setErrors((prev) => ({ ...prev, video_url_b: "" }));
      try {
        const result = await imageService.uploadFile(videofile);
        setValues((prev) => ({ ...prev, video_url_b: result.imageUploadUrl }));
      } catch (err) {
        setErrors((prev) => ({ ...prev, video_url_b: err?.message || "Upload failed." }));
      } finally {
        setUploadingField(null);
      }
    }
  };

  const handleCreate = async () => {
    setLoading(true);
    setErrors({});
    let isErrorExit = false;
    let errorObject = {};
    if (!values.video_url_a && !values.video_url_b) { errorObject.video_url_b = "Video A or B field is required."; isErrorExit = true; }
    if (!values.thumbnail_image_url || !imageFile) { errorObject.thumbnail_image_url = "Video Image field is required."; isErrorExit = true; }
    if (!values.description) { errorObject.description = "Description is required"; isErrorExit = true; }
    if (!values.description_mm) { errorObject.description_mm = "Description_mm is required"; isErrorExit = true; }
    if (!values.video_package_name) { errorObject.video_package_name = "Video Title field is required."; isErrorExit = true; }
    if (!values.fk_exercise_type_name) { errorObject.fk_exercise_type_name = "Exercise Type field is required."; isErrorExit = true; }
    if (!values.fk_experience_level_id) { errorObject.fk_experience_level_id = "Experience field is required."; isErrorExit = true; }
    if (!values.fk_user_subscription_level_id) { errorObject.fk_user_subscription_level_id = "Subscription Level field is required."; isErrorExit = true; }
    const durationRaw = values.duration_in_minute;
    if (durationRaw === undefined || durationRaw === null || String(durationRaw).trim() === "") {
      errorObject.duration_in_minute = "Duration field is required."; isErrorExit = true;
    }
    const prom = values.promotion;
    if (prom === undefined || prom === null || prom === "" || (typeof prom === "string" && prom !== "true" && prom !== "false")) {
      errorObject.promotion = "Promotion field is required."; isErrorExit = true;
    }
    if (isErrorExit) { setErrors({ ...errorObject }); setLoading(false); return; }
    try {
      pendingVideoUrlsRef.current = { video_url_a: values.video_url_a || null, video_url_b: values.video_url_b || null };
      await createVideo({
        variables: {
          ...values,
          video_url_a: values.video_url_a,
          video_url_b: values.video_url_b,
          fk_muscle_type_id: muscleType,
          promotion: values.promotion === "true" || values.promotion === true,
          duration_in_minute: Number(values.duration_in_minute),
          fk_user_subscription_level_id: Number(values.fk_user_subscription_level_id),
        },
      });
    } catch (error) {
      console.log("error : ", error);
    }
  };

  const onChange = (value) => {
    setTextValue(value);
    setValues((prev) => ({ ...prev, description: value.toString("html") }));
  };

  const onChangeMM = (value) => {
    setTextValueMM(value);
    setValues((prev) => ({ ...prev, description_mm: value.toString("html") }));
  };

  const toolbarConfig = {
    display: ["INLINE_STYLE_BUTTONS", "BLOCK_TYPE_BUTTONS", "LINK_BUTTONS", "BLOCK_TYPE_DROPDOWN", "HISTORY_BUTTONS"],
    INLINE_STYLE_BUTTONS: [
      { label: "Bold", style: "BOLD", className: "custom-css-class" },
      { label: "Italic", style: "ITALIC" },
      { label: "Underline", style: "UNDERLINE" },
    ],
    BLOCK_TYPE_DROPDOWN: [
      { label: "Normal", style: "unstyled" },
      { label: "Heading Large", style: "header-one" },
      { label: "Heading Medium", style: "header-two" },
      { label: "Heading Small", style: "header-three" },
    ],
    BLOCK_TYPE_BUTTONS: [
      { label: "UL", style: "unordered-list-item" },
      { label: "OL", style: "ordered-list-item" },
    ],
  };

  return (
    <Box sx={{ bgcolor: "#f8f9fa", minHeight: "100vh" }}>
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          bgcolor: "#fff",
          borderBottom: "1px solid #eee",
          px: 4,
          py: 2.5,
          position: "sticky",
          top: 0,
          zIndex: 10,
        }}
      >
        <Box>
          <Typography variant="h6" fontWeight={700} color="text.primary">Create Video</Typography>
          <Typography variant="caption" color="text.secondary">Upload and configure a new workout video</Typography>
        </Box>
        <Button onClick={handleClose} variant="outlined" color="inherit" size="small" sx={{ borderColor: "#ddd", color: "#555" }}>
          Close
        </Button>
      </Box>

      <Box sx={{ maxWidth: 1040, mx: "auto", px: 3, py: 3 }}>
        {/* Media Upload Section */}
        <Card sx={sectionCard}>
          <Typography variant="subtitle1" fontWeight={700} mb={1}>Media Files</Typography>
          <Divider sx={{ mb: 2.5 }} />
          <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
            <UploadZone
              label="Thumbnail Image"
              accept="JPG, PNG"
              onChange={thumbnailImageChange}
              preview={imagePreview}
              uploading={uploadingField === "thumbnail"}
              error={errors.thumbnail_image_url}
              isVideo={false}
              isUploaded={!!values.thumbnail_image_url}
            />
            <UploadZone
              label="Video A (Main)"
              accept="MP4, WebM"
              onChange={videoChangeA}
              preview={videoAPreview}
              uploading={uploadingField === "videoA"}
              error={errors.video_url_a}
              isVideo={true}
              isUploaded={!!values.video_url_a}
            />
            <UploadZone
              label="Video B (Backup)"
              accept="MP4, WebM"
              onChange={videoChangeB}
              preview={videoBPreview}
              uploading={uploadingField === "videoB"}
              error={errors.video_url_b}
              isVideo={true}
              isUploaded={!!values.video_url_b}
            />
          </Box>
          {errors.video_url_b && !errors.video_url_a && (
            <Typography variant="caption" color="error" sx={{ mt: 1, display: "block" }}>{errors.video_url_b}</Typography>
          )}
        </Card>

        {/* Video Details Section */}
        <Card sx={sectionCard}>
          <Typography variant="subtitle1" fontWeight={700} mb={1}>Video Details</Typography>
          <Divider sx={{ mb: 2.5 }} />
          <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2.5 }}>
            <TextField
              id="video_package_name"
              label="Video Title *"
              fullWidth
              value={values.video_package_name ?? ""}
              onChange={handleChange("video_package_name")}
              error={!!errors.video_package_name}
              helperText={errors.video_package_name}
            />
            <TextField
              type="number"
              id="duration_in_minute"
              label="Duration (minutes) *"
              fullWidth
              value={values.duration_in_minute ?? ""}
              onChange={handleChange("duration_in_minute")}
              error={!!errors.duration_in_minute}
              helperText={errors.duration_in_minute}
            />
            <FormControl fullWidth>
              <InputLabel>Exercise Type *</InputLabel>
              <Select
                label="Exercise Type *"
                value={values.fk_exercise_type_name ?? ""}
                defaultValue=""
                onChange={handleChange("fk_exercise_type_name")}
                error={!!errors.fk_exercise_type_name}
              >
                <MenuItem value="" disabled>Select type</MenuItem>
                {exeTypes?.exercise_types?.map((exe, index) => (
                  <MenuItem key={index} value={exe.exercise_type_name}>{exe?.exercise_type_name}</MenuItem>
                ))}
              </Select>
              {errors.fk_exercise_type_name && <FormHelperText error>{errors.fk_exercise_type_name}</FormHelperText>}
            </FormControl>
            <FormControl fullWidth>
              <InputLabel>Subscription Level *</InputLabel>
              <Select
                label="Subscription Level *"
                value={values.fk_user_subscription_level_id ?? ""}
                defaultValue=""
                onChange={handleChange("fk_user_subscription_level_id")}
                error={!!errors.fk_user_subscription_level_id}
              >
                <MenuItem value="" disabled>Select level</MenuItem>
                {Array.isArray(subscriptionTypes?.user_subscription_level)
                  ? subscriptionTypes.user_subscription_level.map((pt) => (
                      <MenuItem key={pt.id} value={pt.id}>{pt.subscription_type}</MenuItem>
                    ))
                  : null}
              </Select>
              {errors.fk_user_subscription_level_id && <FormHelperText error>{errors.fk_user_subscription_level_id}</FormHelperText>}
            </FormControl>
            <FormControl fullWidth>
              <InputLabel>Experience Level *</InputLabel>
              <Select
                label="Experience Level *"
                value={values.fk_experience_level_id ?? ""}
                defaultValue=""
                onChange={handleChange("fk_experience_level_id")}
                error={!!errors.fk_experience_level_id}
              >
                <MenuItem value="" disabled>Select level</MenuItem>
                {expLevels?.experience_level?.map((exe, index) => (
                  <MenuItem key={index} value={exe.id}>{exe?.name}</MenuItem>
                ))}
              </Select>
              {errors.fk_experience_level_id && <FormHelperText error>{errors.fk_experience_level_id}</FormHelperText>}
            </FormControl>
            <FormControl fullWidth>
              <InputLabel>Promotion *</InputLabel>
              <Select
                label="Promotion *"
                value={values.promotion ?? ""}
                onChange={handleChange("promotion")}
                error={!!errors.promotion}
              >
                <MenuItem value="" disabled>Select</MenuItem>
                <MenuItem value="true">Yes</MenuItem>
                <MenuItem value="false">No</MenuItem>
              </Select>
              {errors.promotion && <FormHelperText error>{errors.promotion}</FormHelperText>}
            </FormControl>
            <FormControl fullWidth sx={{ gridColumn: "1 / -1" }}>
              <InputLabel>Muscle Type</InputLabel>
              <Select
                label="Muscle Type"
                multiple
                value={muscleType}
                onChange={handleChangesMuscleType}
                input={<OutlinedInput label="Muscle Type" />}
                renderValue={(selected) => selected.map((uuid) => getSubtypeNameById(uuid)).join(", ")}
                MenuProps={MenuProps}
              >
                {muslceTypess?.muscle_types?.map((names) => (
                  <MenuItem key={names.id} value={names.id}>
                    <Checkbox checked={muscleType.includes(names.id)} />
                    <ListItemText primary={names.name} />
                  </MenuItem>
                ))}
              </Select>
              {errors.fk_muscle_type_id && <FormHelperText error>{errors.fk_muscle_type_id}</FormHelperText>}
            </FormControl>
          </Box>
        </Card>

        {/* Descriptions Section */}
        <Card sx={sectionCard}>
          <Typography variant="subtitle1" fontWeight={700} mb={1}>Descriptions</Typography>
          <Divider sx={{ mb: 2.5 }} />
          <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2.5 }}>
            <Box>
              <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ mb: 1, display: "block", textTransform: "uppercase", letterSpacing: "0.05em" }}>
                English *
              </Typography>
              <RichTextEditor className="description-text" onChange={onChange} value={textValue} toolbarConfig={toolbarConfig} />
              {errors.description && <FormHelperText error>{errors.description}</FormHelperText>}
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ mb: 1, display: "block", textTransform: "uppercase", letterSpacing: "0.05em" }}>
                Myanmar *
              </Typography>
              <RichTextEditor className="description-text" onChange={onChangeMM} value={textValueMM} toolbarConfig={toolbarConfig} />
              {errors.description_mm && <FormHelperText error>{errors.description_mm}</FormHelperText>}
            </Box>
          </Box>
        </Card>

        {/* Footer Actions */}
        <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2, pt: 1, pb: 4 }}>
          <Button onClick={handleClose} variant="outlined" color="inherit" size="large" sx={{ borderColor: "#ddd", color: "#555", minWidth: 100 }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            size="large"
            disabled={loading || !!uploadingField}
            onClick={handleCreate}
            sx={{
              minWidth: 130,
              bgcolor: "#ff5722",
              "&:hover": { bgcolor: "#e64a19" },
              "&.Mui-disabled": { bgcolor: "#ffccbc", color: "#fff" },
            }}
          >
            {loading ? <><CircularProgress size={18} color="inherit" sx={{ mr: 1 }} />Creating...</> : "Create Video"}
          </Button>
        </Box>
      </Box>

      {showAlert.message && !showAlert.isError && (
        <Alert sx={{ position: "fixed", bottom: "1em", right: "1em" }} severity="success">{showAlert.message}</Alert>
      )}
      {showAlert.message && showAlert.isError && (
        <Alert sx={{ position: "fixed", bottom: "1em", right: "1em" }} severity="error">{showAlert.message}</Alert>
      )}
    </Box>
  );
};
export default CreateVideo;
